import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Award, TrendingUp, TrendingDown, Minus, MapPin, Users } from "lucide-react";
import RetentionQualityBlock from "./RetentionQualityBlock";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "last30", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
  { value: "lifetime", label: "Lifetime" },
];
const fmtGBP = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);
const MomentumIcon = ({ value }) => value === "improving" ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : value === "declining" ? <TrendingDown className="h-3.5 w-3.5 text-rose-600" /> : <Minus className="h-3.5 w-3.5 text-slate-400" />;
const momCls = (v) => v === "improving" ? "bg-emerald-100 text-emerald-700" : v === "declining" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600";

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function FundraiserPerformance({ open, onOpenChange, fundraiserId, onAction }) {
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get("/locations")
      .then(r => setLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLocations([]));
  }, [open]);

  useEffect(() => {
    if (!open || !fundraiserId) return;
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (period === "custom" && customFrom && customTo) {
      params.set("custom_from", customFrom); params.set("custom_to", customTo);
    }
    if (locationId) params.set("location_id", locationId);
    apiClient.get(`/fundraisers/${fundraiserId}/performance?${params.toString()}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, fundraiserId, period, customFrom, customTo, locationId]);

  const p = data?.profile; const k = data?.kpis; const m = data?.productMix;
  const t = data?.trends; const r = data?.reliability; const q = data?.quality;
  const e = data?.earnings; const loc = data?.locations; const badges = data?.badges || [];
  const canShowCustom = useMemo(() => period === "custom", [period]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="fundraiser-perf-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-indigo-600" />
            Fundraiser Performance
            {p && <span className="text-slate-900 font-semibold">· {p.name}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 py-2 border-b" data-testid="fp-filter-bar">
          {PERIODS.map(x => (
            <Button key={x.value} size="sm" variant={period === x.value ? "default" : "outline"}
                    onClick={() => setPeriod(x.value)} data-testid={`fp-period-${x.value}`}
                    className={period === x.value ? "bg-slate-900 text-white" : ""}>
              {x.label}
            </Button>
          ))}
          {canShowCustom && (
            <div className="flex items-center gap-1 ml-2" data-testid="fp-custom-range">
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-8 w-36" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-8 w-36" />
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto" data-testid="fp-location-filter">
            <span className="text-xs text-slate-500">Location</span>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs min-w-[180px]"
              data-testid="fp-location-select"
            >
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        {loading && <div className="py-8 text-center text-slate-500">Loading performance...</div>}
        {!loading && data && (data.kpis?.totalSignups === 0 && data.kpis?.activeShifts === 0 && data.kpis?.cancellations === 0) && (
          <div className="py-10 text-center text-slate-500 border border-dashed border-slate-200 rounded-lg mt-2" data-testid="fp-empty-state">
            No activity for selected location/date range.
          </div>
        )}
        {!loading && data && !(data.kpis?.totalSignups === 0 && data.kpis?.activeShifts === 0 && data.kpis?.cancellations === 0) && (
          <div className="space-y-4 mt-2">
            {/* A — Profile */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="fp-profile">
              <div><div className="text-xs text-slate-500">Name</div><div className="font-semibold">{p?.name}</div></div>
              <div><div className="text-xs text-slate-500">Role</div><div>{p?.role}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><Badge className={p?.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{p?.status}</Badge></div>
              <div><div className="text-xs text-slate-500">Start date</div><div className="text-xs">{p?.startDate ? new Date(p.startDate).toLocaleDateString("en-GB") : "—"}</div></div>
              <div><div className="text-xs text-slate-500">Leader</div><div>{p?.leader?.name || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Pay per signup</div><div>{fmtGBP(p?.payPerSignup)}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Assigned campaigns</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(p?.assignedCampaigns || []).map(c => <Badge key={c.id} variant="outline" className="text-[10px]">{c.name}</Badge>)}
                  {(p?.assignedCampaigns || []).length === 0 && <span className="text-xs text-slate-400">None</span>}
                </div>
              </div>
            </CardContent></Card>

            {/* B — KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="fp-kpis">
              <Stat label="Total signups" value={k?.totalSignups ?? 0} testId="fp-kpi-total" />
              <Stat label="Net signups" value={k?.netSignups ?? 0} testId="fp-kpi-net" />
              <Stat label="Cancellations" value={k?.cancellations ?? 0} testId="fp-kpi-cancel" />
              <Stat label="Est. revenue" value={fmtGBP(k?.estRevenue)} testId="fp-kpi-revenue" />
              <Stat label="Signups / shift" value={k?.signupsPerShift ?? 0} testId="fp-kpi-sigshift" />
              <Stat label="Signups / day" value={k?.signupsPerDay ?? 0} testId="fp-kpi-sigday" />
              <Stat label="Signups / hour" value={k?.signupsPerHour ?? 0} testId="fp-kpi-sighr" />
              <Stat label="Active shifts" value={k?.activeShifts ?? 0} testId="fp-kpi-shifts" />
            </div>

            {/* B2 — Retention Quality (scoped, uses /fundraisers/:id/retention) */}
            {fundraiserId && (
              <RetentionQualityBlock endpoint={`/fundraisers/${fundraiserId}/retention`} testId="fp-retention-quality" />
            )}

            {/* C — Product Mix */}
            <Card><CardContent className="p-4" data-testid="fp-mix">
              <div className="text-xs uppercase text-slate-500 mb-2">Product mix</div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                <div><div className="text-xs text-slate-500">Lottery singles</div><div className="font-semibold">{m?.lotterySingle ?? 0}</div></div>
                <div><div className="text-xs text-slate-500">Lottery doubles</div><div className="font-semibold">{m?.lotteryDouble ?? 0}</div></div>
                <div><div className="text-xs text-slate-500">Regular giving</div><div className="font-semibold">{m?.regularGiving ?? 0}</div></div>
                <div><div className="text-xs text-slate-500">One-off</div><div className="font-semibold">{m?.oneOff ?? 0}</div></div>
                <div><div className="text-xs text-slate-500">Avg value</div><div className="font-semibold">{fmtGBP(m?.avgValue)}</div></div>
                <div><div className="text-xs text-slate-500">Highest</div><div className="font-semibold">{fmtGBP(m?.maxValue)}</div></div>
              </div>
            </CardContent></Card>

            {/* D — Trends */}
            <Card><CardContent className="p-4" data-testid="fp-trends">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase text-slate-500">Trends</div>
                <div className="flex gap-2">
                  <Badge className={momCls(t?.momentum7)}><MomentumIcon value={t?.momentum7} /> 7-day: {t?.momentum7}</Badge>
                  <Badge className={momCls(t?.momentum30)}><MomentumIcon value={t?.momentum30} /> 30-day: {t?.momentum30}</Badge>
                </div>
              </div>
              <div className="h-40">
                <ResponsiveContainer>
                  <LineChart data={t?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={d => d?.slice(5)} tick={{ fontSize: 10 }} interval={"preserveStartEnd"} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="signups" stroke="#6366f1" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>

            {/* E — Locations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fp-locations">
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Best locations</div>
                {(loc?.best || []).length ? (
                  <table className="w-full text-xs"><tbody>
                    {loc.best.map(x => <tr key={x.locationId} className="border-b last:border-0"><td className="py-1">{x.locationName}</td><td className="text-right">{x.signups}</td><td className="text-right text-slate-500">{fmtGBP(x.revenue)}</td></tr>)}
                  </tbody></table>
                ) : <div className="text-xs text-slate-400">No location data</div>}
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs uppercase text-slate-500 mb-2">Weakest locations</div>
                {(loc?.weakest || []).length ? (
                  <table className="w-full text-xs"><tbody>
                    {loc.weakest.map(x => <tr key={x.locationId} className="border-b last:border-0"><td className="py-1">{x.locationName}</td><td className="text-right">{x.signups}</td></tr>)}
                  </tbody></table>
                ) : <div className="text-xs text-slate-400">—</div>}
              </CardContent></Card>
            </div>

            {/* F — Reliability */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fp-reliability">
              <Stat label="Attendance %" value={`${r?.attendancePct ?? 0}%`} />
              <Stat label="Completed shifts" value={r?.completedShifts ?? 0} />
              <Stat label="No-shows" value={r?.noShows ?? 0} />
              <Stat label="Late starts" value={r?.lateStarts ?? 0} sub="If tracked" />
              <Stat label="Active days" value={r?.activeDays ?? 0} />
            </CardContent></Card>

            {/* G — Quality */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fp-quality">
              <Stat label="Flagged signups" value={q?.flaggedSignups ?? 0} />
              <Stat label="Cancellation %" value={`${q?.cancellationPct ?? 0}%`} />
              <Stat label="Training complete" value={q?.trainingComplete ? "Yes" : "No"} />
              <Stat label="Compliance" value={Object.values(q?.compliance || {}).filter(Boolean).length ? "Partial / complete" : "Incomplete"} />
            </CardContent></Card>

            {/* H — Earnings */}
            <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fp-earnings">
              <Stat label="This week est." value={fmtGBP(e?.currentWeekEstPay)} />
              <Stat label="Period est." value={fmtGBP(e?.periodEstPay)} />
              <Stat label="Last payroll" value={e?.lastPayroll ? fmtGBP(e.lastPayroll.grossPay) : "—"} sub={e?.lastPayroll?.createdAt ? new Date(e.lastPayroll.createdAt).toLocaleDateString("en-GB") : ""} />
              <Stat label="Lifetime earned" value={fmtGBP(e?.lifetimeEarned)} />
            </CardContent></Card>

            {/* I — Coaching badges */}
            <Card><CardContent className="p-4" data-testid="fp-badges">
              <div className="text-xs uppercase text-slate-500 mb-2">Coaching badges</div>
              <div className="flex flex-wrap gap-2">
                {badges.length === 0 && <span className="text-xs text-slate-400">No badges yet</span>}
                {badges.map(b => <Badge key={b} variant="outline" className="text-[11px]">{b}</Badge>)}
              </div>
            </CardContent></Card>

            {/* J — Actions */}
            <div className="flex flex-wrap gap-2" data-testid="fp-actions">
              <Button size="sm" variant="outline" onClick={() => onAction?.("view-shifts", fundraiserId)}>View shifts</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("view-signups", fundraiserId)}>View signups</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("edit-role", fundraiserId)}>Edit role</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("reset-password", fundraiserId)}>Reset password</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("assign-campaign", fundraiserId)}>Assign campaign</Button>
              <Button size="sm" variant="outline" onClick={() => onAction?.("open-planner", fundraiserId)}>
                <Users className="h-3.5 w-3.5 mr-1" /> Open planner
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
